import React from 'react'
import styled from '@emotion/styled'
import { colors, radius, spacing } from '@mobi/component-library/Theme/Common'
import { TicketDisplayContext, TicketDisplayContextType } from '@core/Areas/AccountActivity/context'
import { BetInfo } from '@core/Areas/AccountActivity/Components/Ticket/Components/BetInfo'
import { SelectionInfo } from '@core/Areas/AccountActivity/Components/Ticket/Components/Details/Components/SelectionInfo'
import {
  getTicketLayoutType,
  type TicketLayoutType,
} from '@core/Areas/AccountActivity/Components/Ticket/helpers/legTypes'
import type { BetLoopSharedBet } from '@core/Areas/BetLoop/types'
import { trackBetLoopExpandSelection } from '@core/Areas/BetLoop/helpers/analytics'
import { getBetLoopTicketStatus } from '@core/Areas/BetLoop/helpers/getBetLoopTicketStatus'
import { HeightTruncateWrapper } from './Components/HeightTruncateWrapper'
import { dayjs } from '@mobi/utils/date'

const enum LocalConstants {
  ShareBetCardContainerClass = 'bet-selection-display__card',
  SharedBetWinResultClass = 'bet-selection-display__win-result',
  SharedBetNoPayoutResultClass = 'bet-selection-display__no-payout-result',
}

export const BetSelectionDisplay: React.FC<{
  sharedBet: BetLoopSharedBet
  onPendingStatusChange(): void
  shouldDisableHeightTruncate?: true
  isForExternalShare?: boolean
}> = ({ sharedBet, onPendingStatusChange, shouldDisableHeightTruncate, isForExternalShare }) => {
  const ticket = sharedBet.ticket
  const ticketTypeLayout = getTicketLayoutType(ticket)

  const isHeightTruncateEnabled =
    !shouldDisableHeightTruncate && shouldEnableHeightTruncate(ticket, ticketTypeLayout)

  const handleOnChangeCallback = (expanded: boolean) => {
    trackBetLoopExpandSelection(expanded ? 'enable' : 'disable')
  }

  const isResulted = sharedBet.isResulted
  const ticketResultStatus = getBetLoopTicketStatus(ticket)
  const winShareResult = isResulted && ticketResultStatus === 'Won'
  const isPending = ticketResultStatus === 'Pending'
  const noPayoutShareResult = isResulted && ticketResultStatus !== 'Won'
  const hasMultipleLegToteBetStarted =
    (!isResulted &&
      sharedBet.ticket.allUpStartTimes?.some(ast =>
        dayjs(ast).isBefore(dayjs().subtract(2, 'minute'))
      )) ||
    false

  const contextValue = React.useMemo<TicketDisplayContextType>(() => {
    const forcedStatus =
      (isResulted && isPending) || hasMultipleLegToteBetStarted
        ? 'pending'
        : isResulted
          ? 'resulted'
          : undefined

    return {
      type: 'share',
      data: {
        shouldHideCost: true,
        display: isForExternalShare ? 'external-share' : 'bet-loop',
        forcedStatus,
      },
    }
  }, [isResulted, isForExternalShare, isPending, hasMultipleLegToteBetStarted])

  React.useEffect(() => {
    if (contextValue.type === 'share' && contextValue.data.forcedStatus === 'pending') {
      onPendingStatusChange()
    }
  }, [onPendingStatusChange, contextValue])

  return (
    <WrapperStyled
      style={isHeightTruncateEnabled ? { paddingBottom: '1px' } : undefined}
      className={
        winShareResult
          ? LocalConstants.SharedBetWinResultClass
          : noPayoutShareResult
            ? LocalConstants.SharedBetNoPayoutResultClass
            : ''
      }
    >
      <HeightTruncateWrapper
        isEnabled={isHeightTruncateEnabled}
        onChangeCallback={handleOnChangeCallback}
      >
        <TicketDisplayContext.Provider value={contextValue}>
          <div className={LocalConstants.ShareBetCardContainerClass}>
            <BetInfo
              ticket={ticket}
              status={ticketResultStatus}
              ticketLayoutType={ticketTypeLayout}
            />

            <SelectionInfo ticket={ticket} />
          </div>
        </TicketDisplayContext.Provider>
      </HeightTruncateWrapper>
    </WrapperStyled>
  )
}

// =============
// Local Helpers
// =============

function shouldEnableHeightTruncate(
  ticket: BetLoopSharedBet['ticket'],
  layoutType: TicketLayoutType
): boolean {
  if (layoutType === 'multi' && ticket.legs.length > 2) return true
  if (layoutType === 'single-multiple') {
    const acceptorCount = ticket.legs[0].selections.reduce(
      (count, selection) => selection.selectionDetails[0].acceptorDetails.length + count,
      0
    )
    return acceptorCount > 6
  }

  return false
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx1,
  padding: spacing.sm,
  borderRadius: radius.lgx1,
  backgroundImage: `repeating-linear-gradient(26deg, ${colors.neutral[300]}, ${colors.neutral[300]} 14px, transparent 14px, transparent 28px, ${colors.neutral[300]} 28px), repeating-linear-gradient(116deg, ${colors.neutral[300]}, ${colors.neutral[300]} 14px, transparent 14px, transparent 28px, ${colors.neutral[300]} 28px), repeating-linear-gradient(206deg, ${colors.neutral[300]}, ${colors.neutral[300]} 14px, transparent 14px, transparent 28px, ${colors.neutral[300]} 28px), repeating-linear-gradient(296deg, ${colors.neutral[300]}, ${colors.neutral[300]} 14px, transparent 14px, transparent 28px, ${colors.neutral[300]} 28px)`,
  backgroundSize: '1px 100%, 100% 1px, 1px 100% , 100% 1px',
  backgroundPosition: '0 0, 0 0, 100% 0, 0 100%',
  backgroundRepeat: 'no-repeat',

  [`&.${LocalConstants.SharedBetWinResultClass}`]: {
    filter: `drop-shadow(0 0 ${spacing.smx2} ${colors.celery[300]})`,
    border: `0.3rem solid ${colors.celery[500]}`,
    background: colors.white,
  },

  [`&.${LocalConstants.SharedBetNoPayoutResultClass}`]: {
    background: 'none',
    border: `0.3rem solid ${colors.neutral[300]}`,
  },

  [`.${LocalConstants.ShareBetCardContainerClass}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.smx1,
  },
})
